import { useMemo } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";

import { useDeviceTemplatesTableRows } from "../../../../pages/system-settings/tabs/devices/hooks/useDeviceTemplatesTableRows";

interface Template {
  id: string;
  templateData: string;
  numberOfNodes: number;
}

interface IDeviceTemplateSelect {
  templateId?: string | null;
  label?: string;
  onTemplateChange: (template: Template) => void;
  error?: boolean;
  helperText?: string;
}

interface IDeviceTemplateOption {
  title: string;
  value: Template;
}

interface DeviceTemplateRow {
  id?: string;
  name?: string;
  templateData?: string;
  numberOfNodes?: number;
}

const DeviceTemplateSelect = ({
  templateId,
  label,
  onTemplateChange,
  error = false,
  helperText = "",
}: IDeviceTemplateSelect): JSX.Element => {
  const { rows, loading } = useDeviceTemplatesTableRows();

  const options = useMemo((): IDeviceTemplateOption[] => {
    return (
      rows?.map((item: DeviceTemplateRow): IDeviceTemplateOption => {
        return {
          title: item.name ?? "",
          value: {
            id: item.id ?? "",
            templateData: item.templateData ?? "",
            numberOfNodes: item.numberOfNodes ?? 0,
          },
        };
      }) ?? []
    );
  }, [rows]);

  const memoizedValue = useMemo((): IDeviceTemplateOption | null => {
    if (!templateId) {
      return null;
    }

    return options.find(option => option.value.id === templateId) ?? null;
  }, [templateId, options]);

  return (
    <Autocomplete
      fullWidth
      disableClearable
      loading={loading}
      value={memoizedValue as IDeviceTemplateOption}
      onChange={(_event, newValue: IDeviceTemplateOption | null): void => {
        if (newValue) {
          onTemplateChange(newValue.value);
        }
      }}
      getOptionLabel={(option: IDeviceTemplateOption): string =>
        option?.title ?? ""
      }
      isOptionEqualToValue={(option, optionValue): boolean => {
        return option?.value?.id === optionValue?.value?.id;
      }}
      options={options}
      renderInput={(params): JSX.Element => (
        <TextField
          {...params}
          label={label ?? "Device Template"}
          error={error}
          helperText={error ? helperText : ""}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default DeviceTemplateSelect;
